export enum CogAuthorizedScopes {
  PHONE = 'phone',
  EMAIL = 'email',
  OPENID = 'openid',
  PROFILE = 'profile',
  COGNITOADMIN = 'aws.cognito.signin.user.admin',
}

// A single SSO connection (Cognito hosted-UI identity provider).
// A domain may have any number of these, including several of the same `type`
// (e.g. two Active Directory tenants, four Okta orgs) — just add more entries.
export interface SsoConnection {
  idpName: string; // Cognito custom provider name passed to signInWithRedirect
  label: string; // display name shown when more than one connection exists
  type: 'ad' | 'okta' | 'saml' | 'cyberark'; // drives the icon
}

// SSO connections keyed by hostname. Ordered — the list renders in this order.
// This is a plain data map (not an enum) so a domain can hold multiple
// connections of the same service, and so it can later be sourced from a
// remote config file without changing its shape.
export const DOMAIN_SSO: Record<string, SsoConnection[]> = {
  // dev urls
  localhost: [
    { idpName: 'ActiveDirectoryLocal', label: 'Active Directory', type: 'ad' },
    { idpName: 'okta-local', label: 'Okta', type: 'okta' },
    { idpName: 'SAML-local', label: 'SAML', type: 'saml' },
  ],
  'dev.cygovdev.com': [
    { idpName: 'ActiveDirectory', label: 'Active Directory', type: 'ad' },
    { idpName: 'okta-dev', label: 'Okta', type: 'okta' },
    { idpName: 'okta-dev', label: 'SAML', type: 'saml' },
  ],
  'bbins.cygovdev.com': [{ idpName: 'ActiveDirectory-bbins.cygovdev', label: 'Active Directory', type: 'ad' }],
  // production urls
  'live.cermp.com': [
    { idpName: 'ActiveDirectory-Live', label: 'Active Directory', type: 'ad' },
    { idpName: 'Okta-Live', label: 'Okta', type: 'okta' },
    { idpName: 'SAML-Ping-Live', label: 'Ping Identity', type: 'saml' },
  ],
  'cyber-test.bbins.com': [{ idpName: 'ActiveDirectory', label: 'Active Directory', type: 'ad' }],
  'cyber.bbins.com': [{ idpName: 'ActiveDirectory-cyber.bbins', label: 'Active Directory', type: 'ad' }],
  'cwu.cegrc.com': [{ idpName: 'ActiveDirectory-cwu', label: 'Active Directory', type: 'ad' }],
  'dfin.cegrc.com': [{ idpName: 'ActiveDirectory-dfin', label: 'Active Directory', type: 'ad' }],
  'shccares.cegrc.com': [{ idpName: 'ActiveDirectory-shccares', label: 'Active Directory', type: 'ad' }],
  'dmu.cegrc.com': [{ idpName: 'ActiveDirectory-dmu', label: 'Active Directory', type: 'ad' }],
  'instar.cegrc.com': [{ idpName: 'ActiveDirectory-instarsf', label: 'Active Directory', type: 'ad' }],
  'sdbor.cegrc.com': [{ idpName: 'ActiveDirectory-sdbor', label: 'Active Directory', type: 'ad' }],
  'corvus.cegrc.com': [{ idpName: 'Okta-Corvus', label: 'Okta', type: 'okta' }],
  'testuk.cermp.com': [{ idpName: 'Okta-testuk', label: 'Okta', type: 'okta' }],
  'informatica-uat.cegrc.com': [{ idpName: 'Okta-informatica-uat', label: 'Okta', type: 'okta' }],
  'informatica.cegrc.com': [{ idpName: 'Okta-informatica', label: 'Okta', type: 'okta' }],
  'moelis.cegrc.com': [{ idpName: 'Okta-Moelis', label: 'Okta', type: 'okta' }],
  'ca.cegrc.com': [{ idpName: 'SAML-CyberArk', label: 'CyberArk', type: 'cyberark' }],
  'ca-test.cermp.com': [{ idpName: 'SAML-CyberArkTest', label: 'CyberArk', type: 'cyberark' }],
  'ou.cermp.com': [{ idpName: 'SAML-Ping-OU', label: 'Ping Identity', type: 'saml' }],
  'stockman.cegrc.com': [{ idpName: 'SAML-Ping-stockman', label: 'Ping Identity', type: 'saml' }],
  'nebraska.cegrc.com': [{ idpName: 'SAML-Ping-nebraska', label: 'Ping Identity', type: 'saml' }],
  'marriott-test.cegrc.com': [{ idpName: 'Ping-marriott-test', label: 'Ping Identity', type: 'saml' }],
  'marriott.cegrc.com': [{ idpName: 'Ping-marriott', label: 'Ping Identity', type: 'saml' }],
};

export function getFederatedIdentityProvider(): string[] {
  return Object.values(DOMAIN_SSO)
    .flat()
    .map(connection => connection.idpName.toLowerCase());
}
